import React, { useEffect, useState, useCallback } from 'react';
import { Plus, DollarSign, Trash2, Wallet, Lock } from 'lucide-react';
import { get, post, del } from '../api/client';
import { useAuth } from '../hooks/useAuth';
import Modal from './Modal';

const METHODS = ['cash', 'check', 'card', 'online'];

const EMPTY_FUND = {
  name: '',
  description: '',
  is_restricted: false,
};

const EMPTY_DONATION = {
  fund_id: '',
  member_id: '',
  amount: '',
  method: 'cash',
  donation_date: '',
  note: '',
};

const inputCls =
  'w-full rounded-lg border border-slate-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand-500';

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1">{label}</span>
      {children}
    </label>
  );
}

function money(n) {
  return Number(n || 0).toLocaleString(undefined, {
    style: 'currency',
    currency: 'USD',
  });
}

export default function Finance() {
  const { isPastor, isViewer } = useAuth();

  const [funds, setFunds] = useState([]);
  const [donations, setDonations] = useState([]);
  const [members, setMembers] = useState([]);
  const [summary, setSummary] = useState({ total: 0, by_fund: [] });

  const [fundFilter, setFundFilter] = useState('');
  const [loadingDonations, setLoadingDonations] = useState(true);
  const [loadingFunds, setLoadingFunds] = useState(true);
  const [error, setError] = useState('');

  // Modal state — one of: null | { type: 'fund' } | { type: 'donation' }
  const [editing, setEditing] = useState(null);
  const [fundForm, setFundForm] = useState(EMPTY_FUND);
  const [donationForm, setDonationForm] = useState(EMPTY_DONATION);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');

  const loadFunds = useCallback(async () => {
    setLoadingFunds(true);
    try {
      setFunds(await get('/api/v1/finance/funds'));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoadingFunds(false);
    }
  }, []);

  const loadSummary = useCallback(async () => {
    try {
      setSummary(await get('/api/v1/finance/summary'));
    } catch (err) {
      setError(err.message);
    }
  }, []);

  const loadDonations = useCallback(async () => {
    setLoadingDonations(true);
    setError('');
    try {
      const params = {};
      if (fundFilter) params.fund_id = fundFilter;
      setDonations(await get('/api/v1/finance/donations', { params }));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoadingDonations(false);
    }
  }, [fundFilter]);

  useEffect(() => {
    loadFunds();
    loadSummary();
  }, [loadFunds, loadSummary]);

  useEffect(() => {
    const t = setTimeout(loadDonations, 250);
    return () => clearTimeout(t);
  }, [loadDonations]);

  useEffect(() => {
    get('/api/v1/members')
      .then(setMembers)
      .catch(() => {});
  }, []);

  const memberName = (id) => {
    if (!id) return '—';
    const m = members.find((x) => x.id === id);
    return m ? `${m.first_name} ${m.last_name}` : '—';
  };

  const fundName = (id) => funds.find((f) => f.id === id)?.name || '—';

  // ---- Fund modal ----
  const openNewFund = () => {
    setFundForm(EMPTY_FUND);
    setFormError('');
    setEditing({ type: 'fund' });
  };

  const saveFund = async (e) => {
    e.preventDefault();
    setSaving(true);
    setFormError('');
    try {
      await post('/api/v1/finance/funds', {
        name: fundForm.name,
        description: fundForm.description || undefined,
        is_restricted: fundForm.is_restricted,
      });
      setEditing(null);
      loadFunds();
      loadSummary();
    } catch (err) {
      setFormError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const removeFund = async (f) => {
    if (!window.confirm(`Delete fund "${f.name}"?`)) return;
    try {
      await del(`/api/v1/finance/funds/${f.id}`);
      loadFunds();
      loadSummary();
    } catch (err) {
      alert(err.message);
    }
  };

  // ---- Donation modal ----
  const openNewDonation = () => {
    setDonationForm({ ...EMPTY_DONATION, fund_id: funds[0]?.id || '' });
    setFormError('');
    setEditing({ type: 'donation' });
  };

  const saveDonation = async (e) => {
    e.preventDefault();
    setSaving(true);
    setFormError('');
    try {
      const payload = {
        fund_id: donationForm.fund_id,
        amount: Number(donationForm.amount),
        method: donationForm.method,
      };
      if (donationForm.member_id) payload.member_id = donationForm.member_id;
      if (donationForm.donation_date)
        payload.donation_date = new Date(donationForm.donation_date).toISOString();
      if (donationForm.note) payload.note = donationForm.note;
      await post('/api/v1/finance/donations', payload);
      setEditing(null);
      loadDonations();
      loadSummary();
    } catch (err) {
      setFormError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <h2 className="text-xl font-semibold text-slate-800 dark:text-slate-100">
            Finance
          </h2>
          {isViewer && (
            <span className="text-xs bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300 rounded-full px-2 py-0.5">
              Read-only demo
            </span>
          )}
        </div>
      </div>

      {/* Total given */}
      <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-5 mb-4 flex items-center gap-4">
        <div className="rounded-full bg-brand-100 dark:bg-brand-900/40 text-brand-600 dark:text-brand-300 p-3">
          <DollarSign size={24} />
        </div>
        <div>
          <p className="text-sm text-slate-500 dark:text-slate-400">Total given</p>
          <p className="text-2xl font-semibold text-slate-800 dark:text-slate-100">
            {money(summary.total)}
          </p>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 text-red-700 text-sm rounded-lg px-3 py-2 mb-3">
          {error}
        </div>
      )}

      {/* Funds section */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-100">
            Funds
          </h3>
          {!isViewer && (
            <button
              onClick={openNewFund}
              className="bg-brand-600 hover:bg-brand-700 text-white text-sm font-medium rounded-lg px-3 py-2 flex items-center gap-1"
            >
              <Plus size={16} /> Add fund
            </button>
          )}
        </div>

        {loadingFunds ? (
          <div className="p-8 text-center text-slate-400">Loading…</div>
        ) : funds.length === 0 ? (
          <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-10 text-center text-slate-400 flex flex-col items-center gap-2">
            <Wallet size={28} />
            No funds yet.
          </div>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {funds.map((f) => {
              const fundTotal = summary.by_fund.find(
                (b) => b.fund_id === f.id
              )?.total;
              return (
                <div
                  key={f.id}
                  className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-4"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-2">
                      <h4 className="font-semibold text-slate-800 dark:text-slate-100">
                        {f.name}
                      </h4>
                      {f.is_restricted && (
                        <span
                          className="text-amber-600 dark:text-amber-400"
                          title="Restricted fund"
                        >
                          <Lock size={14} />
                        </span>
                      )}
                    </div>
                    {!isViewer && isPastor && (
                      <button
                        onClick={() => removeFund(f)}
                        className="text-slate-400 hover:text-red-600"
                        title="Delete"
                      >
                        <Trash2 size={16} />
                      </button>
                    )}
                  </div>
                  {f.description && (
                    <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                      {f.description}
                    </p>
                  )}
                  <p className="mt-2 text-sm font-medium text-slate-700 dark:text-slate-200">
                    {money(fundTotal)}
                  </p>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Donations section */}
      <div>
        <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
          <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-100">
            Donations
          </h3>
          <div className="flex items-center gap-2">
            <select
              value={fundFilter}
              onChange={(e) => setFundFilter(e.target.value)}
              className="rounded-lg border border-slate-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-100 px-3 py-2 text-sm"
            >
              <option value="">All funds</option>
              {funds.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.name}
                </option>
              ))}
            </select>
            {!isViewer && (
              <button
                onClick={openNewDonation}
                disabled={funds.length === 0}
                className="bg-brand-600 hover:bg-brand-700 disabled:opacity-60 text-white text-sm font-medium rounded-lg px-3 py-2 flex items-center gap-1"
              >
                <Plus size={16} /> Add donation
              </button>
            )}
          </div>
        </div>

        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
          {loadingDonations ? (
            <div className="p-8 text-center text-slate-400">Loading…</div>
          ) : donations.length === 0 ? (
            <div className="p-10 text-center text-slate-400 flex flex-col items-center gap-2">
              <DollarSign size={28} />
              No donations recorded.
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 dark:bg-slate-700/50 text-slate-500 dark:text-slate-300 text-left">
                <tr>
                  <th className="px-4 py-2 font-medium">Date</th>
                  <th className="px-4 py-2 font-medium">Member</th>
                  <th className="px-4 py-2 font-medium">Fund</th>
                  <th className="px-4 py-2 font-medium text-right">Amount</th>
                  <th className="px-4 py-2 font-medium">Method</th>
                </tr>
              </thead>
              <tbody>
                {donations.map((d) => (
                  <tr
                    key={d.id}
                    className="border-t border-slate-100 dark:border-slate-700"
                  >
                    <td className="px-4 py-2 text-slate-500 dark:text-slate-400">
                      {new Date(d.donation_date).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-2 text-slate-800 dark:text-slate-100">
                      {memberName(d.member_id)}
                    </td>
                    <td className="px-4 py-2 text-slate-500 dark:text-slate-400">
                      {fundName(d.fund_id)}
                    </td>
                    <td className="px-4 py-2 text-right font-medium text-slate-800 dark:text-slate-100">
                      {money(d.amount)}
                    </td>
                    <td className="px-4 py-2">
                      <span className="inline-block rounded-full px-2 py-0.5 text-xs font-medium bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300">
                        {d.method}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Fund modal */}
      {editing?.type === 'fund' && (
        <Modal
          title="Add fund"
          onClose={() => setEditing(null)}
          footer={
            <>
              <button
                onClick={() => setEditing(null)}
                className="px-4 py-2 text-sm text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white"
              >
                Cancel
              </button>
              <button
                onClick={saveFund}
                disabled={saving}
                className="bg-brand-600 hover:bg-brand-700 disabled:opacity-60 text-white text-sm font-medium rounded-lg px-4 py-2"
              >
                {saving ? 'Saving…' : 'Save'}
              </button>
            </>
          }
        >
          <form onSubmit={saveFund} className="space-y-3">
            {formError && (
              <div className="bg-red-50 text-red-700 text-sm rounded-lg px-3 py-2">
                {formError}
              </div>
            )}
            <Field label="Fund name">
              <input
                required
                className={inputCls}
                value={fundForm.name}
                onChange={(e) => setFundForm({ ...fundForm, name: e.target.value })}
              />
            </Field>
            <Field label="Description">
              <textarea
                rows={3}
                className={inputCls}
                value={fundForm.description}
                onChange={(e) =>
                  setFundForm({ ...fundForm, description: e.target.value })
                }
              />
            </Field>
            <label className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-200">
              <input
                type="checkbox"
                checked={fundForm.is_restricted}
                onChange={(e) =>
                  setFundForm({ ...fundForm, is_restricted: e.target.checked })
                }
              />
              Restricted fund
            </label>
          </form>
        </Modal>
      )}

      {/* Donation modal */}
      {editing?.type === 'donation' && (
        <Modal
          title="Add donation"
          onClose={() => setEditing(null)}
          footer={
            <>
              <button
                onClick={() => setEditing(null)}
                className="px-4 py-2 text-sm text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white"
              >
                Cancel
              </button>
              <button
                onClick={saveDonation}
                disabled={saving}
                className="bg-brand-600 hover:bg-brand-700 disabled:opacity-60 text-white text-sm font-medium rounded-lg px-4 py-2"
              >
                {saving ? 'Saving…' : 'Save'}
              </button>
            </>
          }
        >
          <form onSubmit={saveDonation} className="space-y-3">
            {formError && (
              <div className="bg-red-50 text-red-700 text-sm rounded-lg px-3 py-2">
                {formError}
              </div>
            )}
            <Field label="Fund">
              <select
                required
                className={inputCls}
                value={donationForm.fund_id}
                onChange={(e) =>
                  setDonationForm({ ...donationForm, fund_id: e.target.value })
                }
              >
                <option value="" disabled>
                  Select a fund…
                </option>
                {funds.map((f) => (
                  <option key={f.id} value={f.id}>
                    {f.name}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Member (optional)">
              <select
                className={inputCls}
                value={donationForm.member_id}
                onChange={(e) =>
                  setDonationForm({ ...donationForm, member_id: e.target.value })
                }
              >
                <option value="">— Anonymous / none —</option>
                {members.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.first_name} {m.last_name}
                  </option>
                ))}
              </select>
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Amount">
                <input
                  required
                  type="number"
                  min="0.01"
                  step="0.01"
                  className={inputCls}
                  value={donationForm.amount}
                  onChange={(e) =>
                    setDonationForm({ ...donationForm, amount: e.target.value })
                  }
                />
              </Field>
              <Field label="Method">
                <select
                  className={inputCls}
                  value={donationForm.method}
                  onChange={(e) =>
                    setDonationForm({ ...donationForm, method: e.target.value })
                  }
                >
                  {METHODS.map((m) => (
                    <option key={m} value={m}>
                      {m}
                    </option>
                  ))}
                </select>
              </Field>
            </div>
            <Field label="Date">
              <input
                type="date"
                className={inputCls}
                value={donationForm.donation_date}
                onChange={(e) =>
                  setDonationForm({ ...donationForm, donation_date: e.target.value })
                }
              />
            </Field>
            <Field label="Note">
              <textarea
                rows={2}
                className={inputCls}
                value={donationForm.note}
                onChange={(e) =>
                  setDonationForm({ ...donationForm, note: e.target.value })
                }
              />
            </Field>
          </form>
        </Modal>
      )}
    </div>
  );
}
